import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Map, { Source, Layer, Popup, NavigationControl, ScaleControl } from 'react-map-gl/mapbox';
import Supercluster from 'supercluster';
import 'mapbox-gl/dist/mapbox-gl.css';

const POPUP_STYLE = {
  padding: 0,
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
};

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const CLUSTER_COLORS = {
  small:  '#3b82f6',
  medium: '#f59e0b',
  large:  '#ef4444',
};

function getClusterColor(count) {
  if (count <= 10) return CLUSTER_COLORS.small;
  if (count <= 50) return CLUSTER_COLORS.medium;
  return CLUSTER_COLORS.large;
}

function formatValue(val) {
  if (val == null) return '—';
  if (typeof val === 'number') {
    return Number.isInteger(val)
      ? val.toLocaleString()
      : val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(val);
}

function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, l => l.toUpperCase())
    .trim();
}

function isGeoInternalKey(key) {
  const lk = key.toLowerCase();
  return lk.includes('lat') || lk.includes('lng') || lk.includes('lon') ||
    lk.startsWith('__geo_') || lk === 'latitude' || lk === 'longitude';
}

function detectGeoFields(keys) {
  const latField = keys.find(k => k === '__geo_lat') ||
    keys.find(k => /^lat(?:itude)?$/i.test(k)) ||
    keys.find(k => /(?:^|_)lat(?:$|_)/i.test(k));
  const lngField = keys.find(k => k === '__geo_lng') ||
    keys.find(k => /^lon(?:g(?:itude)?)?$/i.test(k)) ||
    keys.find(k => /(?:^|_)lon(?:g)?(?:$|_)/i.test(k));
  return { latField, lngField };
}

function detectLabelField(keys, latField, lngField) {
  const skip = new Set([latField, lngField, '__geo_lat', '__geo_lng'].filter(Boolean));
  return (
    keys.find(k => !skip.has(k) && /(?:^|_|\b)(city|town|municipality)(?:$|_|\b)/i.test(k)) ||
    keys.find(k => !skip.has(k) && /(?:^|_|\b)(country|nation)(?:$|_|\b)/i.test(k)) ||
    keys.find(k => !skip.has(k) && /(?:^|_|\b)(state|region|province)(?:$|_|\b)/i.test(k)) ||
    keys.find(k => !skip.has(k) && k.toLowerCase().includes('name')) ||
    keys.find(k => !skip.has(k))
  );
}

function detectValueField(keys, latField, lngField, labelField) {
  const skip = new Set([latField, lngField, '__geo_lat', '__geo_lng', labelField].filter(Boolean));
  return (
    keys.find(k => !skip.has(k) && /(?:count|value|total|amount|qty|quantity|orders|revenue|sales)/i.test(k)) ||
    keys.find(k => !skip.has(k) && typeof undefined === 'number') ||
    keys.find(k => !skip.has(k))
  );
}

function flattenBQValues(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = (v && typeof v === 'object' && 'value' in v && Object.keys(v).length === 1) ? v.value : v;
  }
  return out;
}

function buildGeoJSON(data) {
  const keys = Object.keys(data[0] || {});
  const { latField, lngField } = detectGeoFields(keys);
  if (!latField || !lngField) return null;

  const features = data
    .map((row, idx) => {
      const lat = Number(row[latField]?.value ?? row[latField]);
      const lng = Number(row[lngField]?.value ?? row[lngField]);
      if (!lat && !lng) return null;
      return {
        type: 'Feature',
        id: idx,
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { ...flattenBQValues(row), _rowIdx: idx },
      };
    })
    .filter(Boolean);

  if (features.length === 0) return null;
  return { type: 'FeatureCollection', features };
}

function buildGeoJSONFromEChartsOption(echartsOption) {
  const scatterSeries = echartsOption?.series?.find(s => s.type === 'scatter');
  if (!scatterSeries?.data?.length) return null;

  const features = scatterSeries.data
    .filter(d => d.value && (d.value[0] !== 0 || d.value[1] !== 0))
    .map((d, idx) => ({
      type: 'Feature',
      id: idx,
      geometry: { type: 'Point', coordinates: [d.value[0], d.value[1]] },
      properties: {
        ...flattenBQValues(d.rowData || {}),
        __geo_lng: d.value[0],
        __geo_lat: d.value[1],
        _name: d.name,
        _primaryValue: d.value[2],
      },
    }));

  if (!features.length) return null;
  return { type: 'FeatureCollection', features };
}

function fitBoundsToFeatures(features) {
  if (!features || features.length === 0) {
    return { longitude: 0, latitude: 20, zoom: 1.5 };
  }

  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  features.forEach(f => {
    const [lng, lat] = f.geometry.coordinates;
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  const lngSpan = maxLng - minLng;
  const latSpan = maxLat - minLat;
  const span = Math.max(lngSpan, latSpan);

  let zoom = 1.5;
  if (span < 0.1) zoom = 12;
  else if (span < 0.5) zoom = 10;
  else if (span < 2) zoom = 8;
  else if (span < 10) zoom = 6;
  else if (span < 30) zoom = 4;
  else if (span < 80) zoom = 3;
  else if (span < 150) zoom = 2.5;
  else zoom = 1.5;

  return { longitude: centerLng, latitude: centerLat, zoom };
}

// ── Time-step helpers ──────────────────────────────────────────────

function truncateDate(rawVal, step) {
  if (rawVal == null) return null;
  let dateStr = rawVal;
  // BigQuery returns Date/Timestamp as { value: "..." } objects
  if (typeof dateStr === 'object' && dateStr.value != null) dateStr = dateStr.value;
  // Handle numeric Unix timestamps (seconds if < 1e12, else milliseconds)
  if (typeof dateStr === 'number') {
    dateStr = dateStr < 1e12 ? new Date(dateStr * 1000) : new Date(dateStr);
    if (isNaN(dateStr.getTime())) return null;
  } else {
    dateStr = String(dateStr).trim();
    if (!dateStr) return null;
    // For date-only strings (YYYY-MM-DD), append noon UTC to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) dateStr += 'T12:00:00Z';
    // Replace space separator with T (e.g. "2026-03-06 08:00:00" → "2026-03-06T08:00:00Z")
    // Safari doesn't parse space-separated datetime strings
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(dateStr)) {
      dateStr = dateStr.replace(' ', 'T');
      if (!/Z$|[+-]\d{2}:\d{2}$/.test(dateStr)) dateStr += 'Z';
    }
    dateStr = new Date(dateStr);
    if (isNaN(dateStr.getTime())) return null;
  }

  const d = dateStr;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');

  switch (step) {
    case 'year':
      return `${yyyy}`;
    case 'month':
      return `${yyyy}-${mm}`;
    case 'week': {
      const day = d.getUTCDay();
      const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(Date.UTC(yyyy, d.getUTCMonth(), diff));
      return monday.toISOString().slice(0, 10);
    }
    case 'day':
      return `${yyyy}-${mm}-${dd}`;
    case 'hour':
      return `${yyyy}-${mm}-${dd}T${String(d.getUTCHours()).padStart(2, '0')}`;
    default:
      return `${yyyy}-${mm}-${dd}`;
  }
}

function formatStepLabel(stepKey, step) {
  if (!stepKey) return '';
  switch (step) {
    case 'year':
      return stepKey;
    case 'month': {
      const [y, m] = stepKey.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[Number(m) - 1]} ${y}`;
    }
    case 'week':
      return `Week of ${stepKey}`;
    case 'day': {
      const d = new Date(stepKey + 'T00:00:00');
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    case 'hour': {
      const dt = new Date(stepKey + ':00:00');
      return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
        ' ' + dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    default:
      return stepKey;
  }
}

const SPEED_OPTIONS = [1, 2, 4];

function speedIntervalMs(speed) {
  return 1200 / speed;
}

// ── Component ──────────────────────────────────────────────────────

export default function MapboxGeoMap({ data, echartsOption, chartConfig, title }) {
  const mapRef = useRef(null);
  const superclusterRef = useRef(null);
  const [viewState, setViewState] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  // Time animation state
  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);

  const {
    geoCluster,
    geoTimeEnabled,
    geoTimeField,
    geoTimeStep = 'day',
    geoTimeMode = 'trail',
    geoSizeField,
  } = chartConfig || {};

  const shouldCluster = geoCluster !== false;
  const timeEnabled = geoTimeEnabled && geoTimeField;

  // Build base GeoJSON from raw data or echarts scatter data
  const baseGeoJSON = useMemo(() => {
    if (data && data.length > 0) {
      const fromData = buildGeoJSON(data);
      if (fromData) return fromData;
    }
    return buildGeoJSONFromEChartsOption(echartsOption) || null;
  }, [data, echartsOption]);

  // Compute sorted unique date steps from the time field
  const dateSteps = useMemo(() => {
    if (!timeEnabled || !baseGeoJSON) {
      console.log('[MapboxGeoMap] dateSteps skipped:', { timeEnabled, hasGeoJSON: !!baseGeoJSON, geoTimeEnabled, geoTimeField });
      return [];
    }
    const stepSet = new Set();
    let sampleRaw = undefined;
    baseGeoJSON.features.forEach(f => {
      const raw = f.properties[geoTimeField];
      if (sampleRaw === undefined && raw != null) sampleRaw = raw;
      if (raw != null) {
        const key = truncateDate(raw, geoTimeStep);
        if (key) stepSet.add(key);
      }
    });
    const steps = Array.from(stepSet).sort();
    console.log('[MapboxGeoMap] dateSteps computed:', steps.length, 'steps | field:', geoTimeField, '| sample value:', sampleRaw, '| available keys:', Object.keys(baseGeoJSON.features[0]?.properties || {}).join(', '));
    return steps;
  }, [timeEnabled, baseGeoJSON, geoTimeField, geoTimeStep, geoTimeEnabled]);

  // Reset step index when date steps change
  useEffect(() => {
    setStepIndex(0);
    setPlaying(false);
  }, [dateSteps.length, geoTimeField, geoTimeStep]);

  // Filter features by current time step, tagging trail vs current-step points
  const geoJSON = useMemo(() => {
    if (!timeEnabled || dateSteps.length === 0 || !baseGeoJSON) return baseGeoJSON;
    const currentKey = dateSteps[Math.min(stepIndex, dateSteps.length - 1)];
    if (!currentKey) return baseGeoJSON;

    const features = [];
    baseGeoJSON.features.forEach(f => {
      const raw = f.properties[geoTimeField];
      if (raw == null) return;
      const key = truncateDate(raw, geoTimeStep);
      if (!key) return;
      const isCurrentStep = key === currentKey;
      const include = geoTimeMode === 'trail' ? key <= currentKey : isCurrentStep;
      if (include) {
        features.push({
          ...f,
          properties: { ...f.properties, _isCurrentStep: isCurrentStep },
        });
      }
    });

    return { type: 'FeatureCollection', features };
  }, [baseGeoJSON, timeEnabled, dateSteps, stepIndex, geoTimeField, geoTimeStep, geoTimeMode]);

  // Play/pause interval
  useEffect(() => {
    if (!playing || dateSteps.length === 0) return;
    const intervalId = setInterval(() => {
      setStepIndex(prev => {
        if (prev >= dateSteps.length - 1) {
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, speedIntervalMs(playSpeed));
    return () => clearInterval(intervalId);
  }, [playing, playSpeed, dateSteps.length]);

  // ── Dot-size scaling ──────────────────────────────────────────────
  const sizeRange = useMemo(() => {
    if (!geoSizeField || !baseGeoJSON) return null;
    let min = Infinity, max = -Infinity;
    baseGeoJSON.features.forEach(f => {
      const v = Number(f.properties[geoSizeField]);
      if (!isNaN(v) && isFinite(v)) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    });
    if (!isFinite(min) || !isFinite(max) || min === max) return null;
    return [min, max];
  }, [geoSizeField, baseGeoJSON]);

  // Whether trail differentiation applies (only in trail mode with time enabled)
  const isTrailMode = timeEnabled && geoTimeMode === 'trail' && dateSteps.length > 0;

  const circleRadiusPaint = useMemo(() => {
    if (sizeRange && geoSizeField) {
      // data-driven size: zoom is NOT used here so it's safe inside a case expression
      if (!isTrailMode) {
        return ['interpolate', ['linear'], ['to-number', ['get', geoSizeField], 0], sizeRange[0], 4, sizeRange[1], 20];
      }
      return ['case', ['==', ['get', '_isCurrentStep'], true],
        ['interpolate', ['linear'], ['to-number', ['get', geoSizeField], 0], sizeRange[0], 4, sizeRange[1], 20],
        ['interpolate', ['linear'], ['to-number', ['get', geoSizeField], 0], sizeRange[0], 2, sizeRange[1], 10],
      ];
    }
    // zoom-based sizing: Mapbox does NOT allow zoom inside case — use fixed px in trail mode
    if (!isTrailMode) {
      return ['interpolate', ['linear'], ['zoom'], 4, 6, 12, 14];
    }
    return ['case', ['==', ['get', '_isCurrentStep'], true], 9, 4];
  }, [sizeRange, geoSizeField, isTrailMode]);

  const circleColorPaint = useMemo(() => {
    if (!isTrailMode) return CLUSTER_COLORS.small;
    return ['case', ['==', ['get', '_isCurrentStep'], true],
      CLUSTER_COLORS.small,  // current: bright blue
      '#9ca3af',             // trail: muted gray
    ];
  }, [isTrailMode]);

  const circleOpacityPaint = useMemo(() => {
    if (!isTrailMode) return 0.85;
    return ['case', ['==', ['get', '_isCurrentStep'], true], 0.9, 0.45];
  }, [isTrailMode]);

  // Derive field names from features
  const keys = useMemo(() => {
    if (geoJSON?.features?.length) {
      return Object.keys(geoJSON.features[0].properties || {})
        .filter(k => !k.startsWith('_rowIdx'));
    }
    return Object.keys(data?.[0] || {});
  }, [geoJSON, data]);

  const { latField, lngField } = useMemo(() => detectGeoFields(keys), [keys]);
  const labelField = useMemo(() => detectLabelField(keys, latField, lngField), [keys, latField, lngField]);

  // Build supercluster index when data changes
  useEffect(() => {
    if (!geoJSON) return;
    const sc = new Supercluster({ radius: 50, maxZoom: 16, minPoints: 2 });
    sc.load(geoJSON.features);
    superclusterRef.current = sc;
  }, [geoJSON]);

  // Set initial viewport once
  useEffect(() => {
    if (!baseGeoJSON || viewState) return;
    setViewState(fitBoundsToFeatures(baseGeoJSON.features));
  }, [baseGeoJSON, viewState]);

  // Recompute clusters whenever viewport or data changes
  const recomputeClusters = useCallback(() => {
    if (!superclusterRef.current || !viewState) return;
    const { longitude, latitude, zoom } = viewState;
    const z = Math.floor(zoom);
    const span = 180 / Math.pow(2, Math.max(z - 1, 0));
    const bbox = [
      longitude - span, latitude - span * 0.6,
      longitude + span, latitude + span * 0.6,
    ];
    try {
      const pts = superclusterRef.current.getClusters(
        [Math.max(bbox[0], -180), Math.max(bbox[1], -90), Math.min(bbox[2], 180), Math.min(bbox[3], 90)],
        z
      );
      setClusters(pts);
    } catch (e) {
      setClusters([]);
    }
  }, [viewState]);

  useEffect(() => {
    if (shouldCluster) {
      recomputeClusters();
    } else if (geoJSON) {
      setClusters(geoJSON.features);
    }
  }, [shouldCluster, geoJSON, recomputeClusters]);

  const handleClusterClick = useCallback((cluster) => {
    const sc = superclusterRef.current;
    if (!sc) return;
    const [lng, lat] = cluster.geometry.coordinates;
    try {
      const expansionZoom = Math.min(sc.getClusterExpansionZoom(cluster.properties.cluster_id), 20);
      setViewState(vs => ({ ...vs, longitude: lng, latitude: lat, zoom: expansionZoom }));
    } catch (e) {}
  }, []);

  const buildTooltipForFeature = useCallback((feature, lngLat) => {
    const props = feature.properties;
    const [lng, lat] = lngLat || feature.geometry.coordinates;

    if (props.cluster) {
      const sc = superclusterRef.current;
      let rows = [];
      if (sc) {
        try {
          const leaves = sc.getLeaves(props.cluster_id, Infinity);
          const totals = {};
          leaves.forEach(leaf => {
            const p = leaf.properties;
            const dataKeys = keys.filter(k =>
              !isGeoInternalKey(k) && !k.startsWith('_')
            );
            dataKeys.forEach(k => {
              const v = Number(p[k]);
              if (!isNaN(v) && isFinite(v)) {
                totals[k] = (totals[k] || 0) + v;
              }
            });
          });
          rows = Object.entries(totals)
            .filter(([k]) => !isGeoInternalKey(k) && k !== labelField)
            .map(([k, v]) => ({ label: formatLabel(k), value: formatValue(v) }));
        } catch (e) {}
      }
      setTooltip({
        longitude: lng,
        latitude: lat,
        label: `${props.point_count} locations`,
        rows,
      });
      return;
    }

    const labelVal = props[labelField] || props._name || '';
    const dataKeys = keys.filter(k =>
      !isGeoInternalKey(k) &&
      k !== labelField &&
      k !== latField &&
      k !== lngField &&
      !k.startsWith('_')
    );

    setTooltip({
      longitude: lng,
      latitude: lat,
      label: labelVal,
      rows: dataKeys.map(k => ({ label: formatLabel(k), value: formatValue(props[k]) })),
    });
  }, [keys, labelField, latField, lngField]);

  // ── Early returns ─────────────────────────────────────────────────

  if (!MAPBOX_TOKEN) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-50 dark:bg-surface-900 rounded-lg">
        <div className="text-center p-6">
          <p className="text-surface-600 dark:text-surface-400 text-sm font-medium mb-1">
            Mapbox token not configured
          </p>
          <p className="text-surface-500 dark:text-surface-500 text-xs">
            Add <code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">REACT_APP_MAPBOX_TOKEN</code> to your <code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">.env</code> file.
          </p>
        </div>
      </div>
    );
  }

  if (!geoJSON && !baseGeoJSON) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-50 dark:bg-surface-900 rounded-lg">
        <p className="text-surface-500 text-sm">No geographic data available</p>
      </div>
    );
  }

  if (!viewState) return null;

  const showPlayer = timeEnabled && dateSteps.length > 0;
  const currentStepLabel = showPlayer ? formatStepLabel(dateSteps[stepIndex], geoTimeStep) : '';
  const pointCount = geoJSON?.features?.length || 0;
  const noStepsWarning = timeEnabled && dateSteps.length === 0 && baseGeoJSON?.features?.length > 0;

  return (
    <div className="h-full w-full relative rounded-lg overflow-hidden">
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => {
          setViewState(evt.viewState);
        }}
        onMoveEnd={() => shouldCluster && recomputeClusters()}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['unclustered-point', 'cluster-circle']}
        onMouseMove={(evt) => {
          const feature = evt.features?.[0];
          if (feature) {
            buildTooltipForFeature(feature, [evt.lngLat.lng, evt.lngLat.lat]);
          } else {
            setTooltip(null);
          }
        }}
        onMouseLeave={() => setTooltip(null)}
        onClick={(evt) => {
          const feature = evt.features?.[0];
          if (feature?.properties?.cluster) {
            handleClusterClick(feature);
          }
        }}
        cursor={tooltip ? 'pointer' : 'default'}
      >
        <NavigationControl position="top-right" />
        <ScaleControl position="bottom-right" />

        {/* Unclustered points */}
        {!shouldCluster && (
          <Source id="points" type="geojson" data={geoJSON}>
            <Layer
              id="unclustered-point"
              type="circle"
              paint={{
                'circle-radius': circleRadiusPaint,
                'circle-color': circleColorPaint,
                'circle-opacity': circleOpacityPaint,
                'circle-stroke-width': isTrailMode
                  ? ['case', ['==', ['get', '_isCurrentStep'], true], 1.5, 0.5]
                  : 1.5,
                'circle-stroke-color': '#fff',
              }}
            />
          </Source>
        )}

        {/* Clustered points via Supercluster */}
        {shouldCluster && clusters.length > 0 && (
          <Source
            id="clusters"
            type="geojson"
            data={{ type: 'FeatureCollection', features: clusters }}
          >
            <Layer
              id="cluster-circle"
              type="circle"
              filter={['==', ['get', 'cluster'], true]}
              paint={{
                'circle-radius': [
                  'step', ['get', 'point_count'],
                  18,
                  10, 24,
                  50, 30,
                ],
                'circle-color': [
                  'step', ['get', 'point_count'],
                  CLUSTER_COLORS.small,
                  10, CLUSTER_COLORS.medium,
                  50, CLUSTER_COLORS.large,
                ],
                'circle-opacity': 0.85,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
              }}
            />
            <Layer
              id="cluster-count"
              type="symbol"
              filter={['==', ['get', 'cluster'], true]}
              layout={{
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
              }}
              paint={{
                'text-color': '#fff',
              }}
            />
            <Layer
              id="unclustered-point"
              type="circle"
              filter={['!=', ['get', 'cluster'], true]}
              paint={{
                'circle-radius': circleRadiusPaint,
                'circle-color': circleColorPaint,
                'circle-opacity': circleOpacityPaint,
                'circle-stroke-width': isTrailMode
                  ? ['case', ['==', ['get', '_isCurrentStep'], true], 1.5, 0.5]
                  : 1.5,
                'circle-stroke-color': '#fff',
              }}
            />
          </Source>
        )}

        {/* Hover tooltip */}
        {tooltip && (
          <Popup
            longitude={tooltip.longitude}
            latitude={tooltip.latitude}
            anchor="bottom"
            closeButton={false}
            closeOnClick={false}
            onClose={() => setTooltip(null)}
            maxWidth="260px"
            style={{ pointerEvents: 'none' }}
            className="mapbox-tooltip-popup"
          >
            <div style={{
              background: 'rgba(50,50,50,0.92)',
              color: '#fff',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '13px',
              lineHeight: '1.6',
              minWidth: '120px',
            }}>
              {tooltip.label && (
                <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '13px' }}>
                  {tooltip.label}
                </div>
              )}
              {tooltip.rows.map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                  <span style={{ color: '#ccc' }}>{label}:</span>
                  <span style={{ fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
          </Popup>
        )}
      </Map>

      {/* Warning when time is enabled but no date steps found */}
      {noStepsWarning && (
        <div
          style={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(30, 30, 30, 0.88)',
            backdropFilter: 'blur(8px)',
            borderRadius: 12,
            padding: '8px 16px',
            color: '#fbbf24',
            fontSize: 12,
            zIndex: 10,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
          }}
        >
          No dates found in field "{geoTimeField}" — check the field name matches your data.
        </div>
      )}

      {/* Time animation player overlay */}
      {showPlayer && (
        <div
          style={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(30, 30, 30, 0.88)',
            backdropFilter: 'blur(8px)',
            borderRadius: 12,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#fff',
            fontSize: 13,
            zIndex: 10,
            minWidth: 340,
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
            userSelect: 'none',
          }}
        >
          {/* Step back */}
          <button
            onClick={() => { setPlaying(false); setStepIndex(i => Math.max(0, i - 1)); }}
            disabled={stepIndex === 0}
            style={{
              background: 'none', border: 'none', color: stepIndex === 0 ? '#666' : '#fff',
              cursor: stepIndex === 0 ? 'default' : 'pointer', padding: 4, lineHeight: 1, fontSize: 16,
            }}
            title="Previous step"
          >
            ⏮
          </button>

          {/* Play / Pause */}
          <button
            onClick={() => {
              if (stepIndex >= dateSteps.length - 1) {
                setStepIndex(0);
                setPlaying(true);
              } else {
                setPlaying(p => !p);
              }
            }}
            style={{
              background: 'none', border: 'none', color: '#fff',
              cursor: 'pointer', padding: 4, lineHeight: 1, fontSize: 18,
            }}
            title={playing ? 'Pause' : 'Play'}
          >
            {playing ? '⏸' : '▶'}
          </button>

          {/* Step forward */}
          <button
            onClick={() => { setPlaying(false); setStepIndex(i => Math.min(dateSteps.length - 1, i + 1)); }}
            disabled={stepIndex >= dateSteps.length - 1}
            style={{
              background: 'none', border: 'none',
              color: stepIndex >= dateSteps.length - 1 ? '#666' : '#fff',
              cursor: stepIndex >= dateSteps.length - 1 ? 'default' : 'pointer',
              padding: 4, lineHeight: 1, fontSize: 16,
            }}
            title="Next step"
          >
            ⏭
          </button>

          {/* Scrubber */}
          <input
            type="range"
            min={0}
            max={dateSteps.length - 1}
            value={stepIndex}
            onChange={e => { setPlaying(false); setStepIndex(Number(e.target.value)); }}
            style={{ flex: 1, accentColor: '#3b82f6', cursor: 'pointer' }}
          />

          {/* Date label */}
          <span style={{ minWidth: 90, textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 }}>
            {currentStepLabel}
          </span>

          {/* Point count badge */}
          <span style={{
            background: 'rgba(59,130,246,0.25)', borderRadius: 6, padding: '2px 8px',
            fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
          }}>
            {pointCount} pt{pointCount !== 1 ? 's' : ''}
          </span>

          {/* Speed selector */}
          <div style={{ display: 'flex', gap: 2 }}>
            {SPEED_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => setPlaySpeed(s)}
                style={{
                  background: playSpeed === s ? '#3b82f6' : 'rgba(255,255,255,0.12)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 11,
                  cursor: 'pointer',
                  fontWeight: playSpeed === s ? 700 : 400,
                }}
              >
                {s}×
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
